import * as React from "react";
import {
  Box,
  Breadcrumbs,
  Container,
  CssBaseline,
  Grid,
  Toolbar,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Paper,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import WebIcon from "@mui/icons-material/Web";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PeopleIcon from "@mui/icons-material/People";
import StorageIcon from "@mui/icons-material/Storage";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SettingsIcon from "@mui/icons-material/Settings";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import RefreshIcon from "@mui/icons-material/Refresh";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import SquarespacePageHeader from "../components/SquarespacePageHeader";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8787";

const squarespaceFeatures = [
  {
    id: "prices",
    title: "Prices",
    description: "View Squarespace Reseller pricing for domains and website plans",
    icon: <AttachMoneyIcon sx={{ fontSize: 48, color: "primary.main" }} />,
    route: "/dashboard/squarespace/prices",
    status: "available",
  },
  {
    id: "customers",
    title: "Customers",
    description: "View and manage Squarespace customers",
    icon: <PeopleIcon sx={{ fontSize: 48, color: "primary.main" }} />,
    route: "/dashboard/squarespace/customers",
    status: "available",
  },
  {
    id: "orders",
    title: "Orders",
    description: "View and manage Squarespace orders",
    icon: <ShoppingCartIcon sx={{ fontSize: 48, color: "primary.main" }} />,
    route: "/dashboard/squarespace/orders",
    status: "available",
  },
  {
    id: "subscriptions",
    title: "Subscriptions",
    description: "Manage Squarespace subscriptions",
    icon: <SubscriptionsIcon sx={{ fontSize: 48, color: "primary.main" }} />,
    route: "/dashboard/squarespace/subscriptions",
    status: "available",
  },
  {
    id: "websites",
    title: "Websites",
    description: "View all Squarespace websites",
    icon: <WebIcon sx={{ fontSize: 48, color: "primary.main" }} />,
    route: "/dashboard/squarespace/websites",
    status: "available",
  },
  {
    id: "payloads",
    title: "Payloads",
    description: "View and manage inbound webhook payloads",
    icon: <StorageIcon sx={{ fontSize: 48, color: "primary.main" }} />,
    route: "/dashboard/squarespace/payloads",
    status: "available",
  },
  {
    id: "process-order",
    title: "Submit Order",
    description: "Submit Squarespace orders to be stored for processing",
    icon: <PlayArrowIcon sx={{ fontSize: 48, color: "primary.main" }} />,
    route: "/dashboard/squarespace/process-order",
    status: "available",
  },
  {
    id: "docs",
    title: "Documentation",
    description: "Customer, website, and payload flow — how data is stored",
    icon: <MenuBookIcon sx={{ fontSize: 48, color: "primary.main" }} />,
    route: "/dashboard/squarespace/docs",
    status: "available",
  },
];

export default function AdminSquarespace() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [config, setConfig] = React.useState(null);
  const [configLoading, setConfigLoading] = React.useState(true);
  const [configError, setConfigError] = React.useState(null);

  const fetchConfig = React.useCallback(async () => {
    setConfigLoading(true);
    setConfigError(null);
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const response = await fetch(`${API_BASE_URL}/admin/squarespace/config`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        throw new Error('unavailable');
      }
      const data = await response.json();
      setConfig(data);
    } catch {
      setConfigError(
        'Config unavailable. The API may not support this feature yet.'
      );
      setConfig(null);
    } finally {
      setConfigLoading(false);
    }
  }, [getAccessTokenSilently]);

  React.useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleFeatureClick = (feature) => {
    if (feature.status === "available") {
      navigate(feature.route);
    }
  };

  const handleOpenFeature = (route) => {
    navigate(route);
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            position: "relative",
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            background: (theme) =>
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, #1a1f2e 50%, ${theme.palette.background.default} 100%)`
                : `linear-gradient(160deg, #e8eef5 0%, #f5f8fc 30%, ${theme.palette.background.default} 70%, #e0e8f0 100%)`,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: (theme) => {
                const primary = theme.palette.primary.main;
                const secondary = theme.palette.secondary?.main || primary;
                return theme.palette.mode === "dark"
                  ? `radial-gradient(circle at 20% 20%, ${alpha(primary, 0.15)} 0%, transparent 50%), radial-gradient(circle at 80% 80%, ${alpha(secondary, 0.08)} 0%, transparent 50%)`
                  : `radial-gradient(circle at 20% 20%, ${alpha(primary, 0.06)} 0%, transparent 45%), radial-gradient(circle at 85% 70%, ${alpha(secondary, 0.08)} 0%, transparent 45%)`;
              },
              pointerEvents: "none",
            },
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1, position: "relative", zIndex: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  component="a"
                  href="/dashboard"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Home
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  Squarespace
                </Typography>
              </Breadcrumbs>
            </Box>

            <SquarespacePageHeader
              title="Squarespace Management"
              description="Manage Squarespace integrations and features"
              icon={<WebIcon sx={{ color: "primary.main" }} />}
            />

            {/* Config panel - deployment debug */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                borderRadius: 2,
                border: (theme) =>
                  `1px solid ${theme.palette.mode === "dark" ? alpha("#fff", 0.08) : alpha(theme.palette.primary.main, 0.12)}`,
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.background.paper, 0.5)
                    : alpha(theme.palette.primary.main, 0.02),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <SettingsIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                  <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                    Deployment Config
                  </Typography>
                  <Chip label="Debug" size="small" variant="outlined" sx={{ fontSize: "0.7rem" }} />
                </Box>
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={fetchConfig}
                  disabled={configLoading}
                >
                  Refresh
                </Button>
              </Box>
              {configError && (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  {configError}
                </Alert>
              )}
              {configLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Loading config…
                  </Typography>
                </Box>
              ) : config ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Identifier
                    </Typography>
                    <Typography variant="body2" component="div" fontFamily="monospace" sx={{ wordBreak: "break-all" }}>
                      {config.identifier ?? "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Client ID
                    </Typography>
                    <Typography variant="body2" component="div" fontFamily="monospace" sx={{ wordBreak: "break-all" }}>
                      {config.clientId ?? "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Seller ID
                    </Typography>
                    <Typography variant="body2" component="div" fontFamily="monospace" sx={{ wordBreak: "break-all" }}>
                      {config.sellerId ?? "—"}
                    </Typography>
                  </Box>
                </Box>
              ) : null}
            </Paper>

            <Grid container spacing={3}>
              {squarespaceFeatures.map((feature) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={feature.id}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      cursor: feature.status === "available" ? "pointer" : "default",
                      opacity: feature.status === "coming-soon" ? 0.7 : 1,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      ...(feature.id === "process-order" && {
                        background: (theme) =>
                          theme.palette.mode === "dark"
                            ? `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.22)} 0%, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, 0.1)} 100%)`
                            : `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, 0.05)} 100%)`,
                        border: (theme) =>
                          `2px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.3 : 0.18)}`,
                      }),
                      ...(feature.id !== "process-order" && {
                        boxShadow: (theme) =>
                          theme.palette.mode === "dark"
                            ? "0 4px 20px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.05)"
                            : `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.04)}`,
                        border: (theme) =>
                          `1px solid ${theme.palette.mode === "dark" ? alpha("#fff", 0.06) : alpha(theme.palette.primary.main, 0.06)}`,
                      }),
                      ...(feature.id === "process-order" && {
                        boxShadow: (theme) =>
                          theme.palette.mode === "dark"
                            ? `0 4px 24px rgba(0,0,0,0.35), 0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`
                            : `0 4px 24px ${alpha(theme.palette.primary.main, 0.12)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)}`,
                      }),
                      "&:hover": feature.status === "available"
                        ? {
                            transform: feature.id === "process-order"
                              ? "translateY(-8px) scale(1.03)"
                              : "translateY(-8px) scale(1.02)",
                            boxShadow: (theme) => {
                              const c = theme.palette.primary.main;
                              const s = theme.palette.secondary?.main || c;
                              return feature.id === "process-order"
                                ? theme.palette.mode === "dark"
                                  ? `0 28px 56px rgba(0,0,0,0.45), 0 0 0 1.5px ${alpha(s, 0.3)}`
                                  : `0 28px 56px ${alpha(c, 0.2)}, 0 0 0 1.5px ${alpha(c, 0.18)}`
                                : theme.palette.mode === "dark"
                                  ? `0 24px 48px rgba(0,0,0,0.4), 0 0 0 1px ${alpha(s, 0.15)}`
                                  : `0 24px 48px ${alpha(c, 0.15)}, 0 0 0 1px ${alpha(c, 0.08)}`;
                            },
                          }
                        : {},
                    }}
                    onClick={() => handleFeatureClick(feature)}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 60,
                            height: 60,
                            borderRadius: 1.5,
                            background: (theme) =>
                              alpha(
                                theme.palette.primary.main,
                                feature.id === "process-order"
                                  ? theme.palette.mode === "dark"
                                    ? 0.2
                                    : 0.12
                                  : theme.palette.mode === "dark"
                                    ? 0.12
                                    : 0.06
                              ),
                            "& .MuiSvgIcon-root": { fontSize: 36 },
                          }}
                        >
                          {feature.icon}
                        </Box>
                      </Box>
                      <Typography
                        variant="h6"
                        component="h2"
                        fontWeight="bold"
                        gutterBottom
                      >
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </CardContent>
                    {feature.status === "available" && (
                      <CardActions sx={{ px: 2, pb: 2 }}>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenFeature(feature.route);
                          }}
                          sx={{
                            py: 1.25,
                            fontWeight: 600,
                            letterSpacing: "0.02em",
                            transition: "all 0.25s ease",
                            "&:hover": {
                              transform: "translateY(-1px)",
                              boxShadow: (theme) =>
                              `0 6px 20px ${alpha(theme.palette.primary.main, 0.25)}`,
                            },
                          }}
                        >
                          Open
                        </Button>
                      </CardActions>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
          <Footer />
        </Box>
      </Box>
    </>
  );
}
